import Api from '@teko/api';
import { accessTokenInterceptor, handleResponseUnauthorizedInterceptor } from '@teko/auth';
import { cleanObject } from '@teko/utils';
import { CartService as CartService$1 } from '../service.es.js';
import { getError } from './error.es.js';
import { getCartToken, setCartInfoToLocalStorage, defaultCart, setCartToken, storeBuyNowCartKey, getBuyNowKey, removeOldBuyNowCart, storeCartTokenHamper } from './utils.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const cartTokenHeaderKey = "x-cart-token";
const resolveValue = (value) => {
  return typeof value === "function" ? value() : value;
};
const parseToken = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(atob(base64).split("").map(function(c) {
      return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(""));
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};
const parseJWT = (token) => {
  try {
    const parsedToken = parseToken(token);
    if (!!parsedToken) {
      return parsedToken["cid"];
    }
    return null;
  } catch (e) {
    return null;
  }
};
const parseTerminalId = (token) => {
  try {
    const parsedToken = parseToken(token);
    if (!!parsedToken) {
      return parsedToken["tid"];
    }
    return void 0;
  } catch (e) {
    return void 0;
  }
};
class CartService {
  constructor(apiUrl, options) {
    this.cartApi = new Api(apiUrl, getError);
    if (typeof (options == null ? void 0 : options.getCustomAccessToken) === "function") {
      this.cartApi.addInterceptor((request) => {
        var _a;
        request.headers.set("Authorization", `Bearer ${(_a = options.getCustomAccessToken) == null ? void 0 : _a.call(options)}`);
      });
    } else {
      this.cartApi.addInterceptor(accessTokenInterceptor);
    }
    this.cartApi.addInterceptor((req) => {
      if (typeof window === "undefined" || req.headers.get(cartTokenHeaderKey) !== null)
        return;
      if (options == null ? void 0 : options.cartToken) {
        req.headers.set(cartTokenHeaderKey, options == null ? void 0 : options.cartToken);
      } else {
        const cartToken = typeof this.getCartToken === "function" ? this.getCartToken() : getCartToken();
        if (cartToken) {
          req.headers.set(cartTokenHeaderKey, cartToken);
        }
      }
    });
    this.cartApi.addInterceptor((request) => {
      request.headers.set("Accept-Language", (options == null ? void 0 : options.language) || "*");
    });
    this.cartApi.addInterceptor((request) => {
      request.headers.set("use-combo-v2", `${(options == null ? void 0 : options.useComboV2) || false}`);
    });
    this.cartApi.addInterceptor((request) => {
      request.headers.set("use-exclusion-v2", `${(options == null ? void 0 : options.useExclusionV2) || false}`);
    });
    this.location = (options == null ? void 0 : options.location) || void 0;
    this.terminal = (options == null ? void 0 : options.terminal) || void 0;
    this.terminalId = (options == null ? void 0 : options.terminalId) || void 0;
    this.getFullCart = (options == null ? void 0 : options.getFullCart) || void 0;
    this.v1 = new CartService$1(apiUrl);
    this.cartApi.addResponseInterceptor(handleResponseUnauthorizedInterceptor);
  }
  getCartId(cartToken) {
    var _a;
    const token = cartToken || ((_a = this.getCartToken) == null ? void 0 : _a.call(this));
    return typeof token === "string" ? parseJWT(token) : void 0;
  }
  getParams(configs) {
    return {
      getFullCart: this.getFullCart,
      location: this.location,
      terminal: !(configs == null ? void 0 : configs.noTerminal) ? resolveValue(this.terminal) : void 0,
      terminalId: !(configs == null ? void 0 : configs.noTerminalId) ? resolveValue(this.terminalId) : void 0,
      hasAllApplicableOrderPromotions: this.hasAllApplicableOrderPromotions
    };
  }
  setParams({
    getFullCart,
    location,
    terminal,
    terminalId,
    getCartToken: getCartToken2,
    hasAllApplicableOrderPromotions
  }) {
    this.getFullCart = typeof getFullCart !== "undefined" ? getFullCart : this.getFullCart;
    this.location = typeof location !== "undefined" ? location : this.location;
    this.terminal = typeof terminal !== "undefined" ? terminal : this.terminal;
    this.terminalId = typeof terminalId !== "undefined" ? terminalId : this.terminalId;
    this.getCartToken = typeof getCartToken2 !== "undefined" ? getCartToken2 : this.getCartToken;
    this.hasAllApplicableOrderPromotions = typeof hasAllApplicableOrderPromotions !== "undefined" ? hasAllApplicableOrderPromotions : this.hasAllApplicableOrderPromotions;
  }
  async getCart(oldTerminalId) {
    const { data, headers } = await this.cartApi.get("/api/v2/carts", {
      params: __spreadProps(__spreadValues({}, this.getParams()), { oldTerminalId })
    });
    if (!!oldTerminalId) {
      const newToken = headers[cartTokenHeaderKey];
      const oldToken = typeof this.getCartToken === "function" ? this.getCartToken() : getCartToken();
      if (oldToken)
        setCartInfoToLocalStorage(this.getCartId(newToken), {
          newCartToken: newToken,
          oldCartToken: oldToken,
          terminalId: parseTerminalId(newToken),
          cartDiffDetails: data == null ? void 0 : data.message
        });
    }
    return __spreadProps(__spreadValues({}, data == null ? void 0 : data.data), {
      message: !!oldTerminalId ? data == null ? void 0 : data.message : void 0
    }) || defaultCart;
  }
  async applyPromotion(applyPromotionRequest) {
    const { ppmPromotionId, ppmCode, itemId, isForceApply, promotionSource } = applyPromotionRequest;
    const res = await this.cartApi.put("/api/v2/carts/promotions", {
      ppmPromotionId,
      ppmCode,
      itemId,
      isForceApply,
      promotionSource
    }, {
      params: this.getParams()
    });
    return res == null ? void 0 : res.data;
  }
  async removePromotion(removePromotionRequest) {
    const { cartPromotionId } = removePromotionRequest;
    const res = await this.cartApi.delete("/api/v2/carts/promotions", {
      cartPromotionId
    }, {
      params: this.getParams()
    });
    return res == null ? void 0 : res.data;
  }
  async recordOrder(recordOrderRequest, cartToken) {
    const res = await this.cartApi.post("/api/v2/carts/record", recordOrderRequest, {
      params: this.getParams(),
      headers: cartToken ? { [cartTokenHeaderKey]: cartToken } : {}
    });
    return res == null ? void 0 : res.data;
  }
  async getAllCartTokens(configs) {
    var _a, _b;
    const res = await this.cartApi.get("/api/v2/cart-tokens", {
      params: this.getParams(configs)
    });
    if (!Array.isArray((_a = res.data) == null ? void 0 : _a.data)) {
      return [];
    }
    return (_b = res.data) == null ? void 0 : _b.data;
  }
  async createNewCart({
    deviceSerialNumber
  }) {
    var _a;
    const res = await this.cartApi.post("/api/v2/carts", {}, {
      params: __spreadProps(__spreadValues({}, this.getParams()), {
        deviceSerialNumber
      })
    });
    const data = ((_a = res == null ? void 0 : res.data) == null ? void 0 : _a.data) || { cart: defaultCart, cartToken: "" };
    return data;
  }
  async deleteCart(cartToken) {
    await this.cartApi.delete("/api/v2/carts", void 0, {
      params: this.getParams(),
      headers: cartToken ? { [cartTokenHeaderKey]: cartToken } : {}
    });
  }
  async loadConfirmationCart({
    deviceSerialNumber
  }) {
    var _a;
    const res = await this.cartApi.get("/api/v2/carts/confirmation", {
      params: __spreadProps(__spreadValues({}, this.getParams()), {
        deviceSerialNumber
      })
    });
    return ((_a = res.data) == null ? void 0 : _a.data) || defaultCart;
  }
  async getDeliveryOptions({
    deliveryType,
    customerProfileId
  }) {
    var _a;
    const res = await this.cartApi.post("/api/v2/carts/delivery-options", { deliveryType, customerProfileId }, { params: this.getParams() });
    return ((_a = res.data) == null ? void 0 : _a.data) || {};
  }
  async setDeliveryInfo({
    address,
    addressId,
    deliveryType,
    districtId,
    districtName,
    email,
    fullAddress,
    name,
    phone,
    provinceId,
    provinceName,
    siteId,
    wardId,
    wardName,
    getAllApplicablePromotions
  }, optionalParams) {
    var _b;
    const { cartToken, terminalId } = optionalParams || {};
    const generatedFullAddress = [address, wardName, districtName, provinceName].filter(Boolean).join(", ");
    const _a = this.getParams(), { hasAllApplicableOrderPromotions } = _a, rest = __objRest(_a, ["hasAllApplicableOrderPromotions"]);
    const res = await this.cartApi.put("/api/v2/carts/delivery-info", cleanObject({
      addressId,
      deliveryType,
      districtId,
      fullAddress: fullAddress || generatedFullAddress,
      name,
      phone,
      provinceId,
      siteId,
      wardId,
      email
    }), {
      params: __spreadProps(__spreadValues({}, rest), {
        getAllApplicablePromotions: getAllApplicablePromotions || hasAllApplicableOrderPromotions,
        terminalId: terminalId || rest.terminalId
      }),
      headers: cartToken ? { [cartTokenHeaderKey]: cartToken } : {}
    });
    return ((_b = res.data) == null ? void 0 : _b.data) || defaultCart;
  }
  async changeItemPrice({
    itemId,
    consultationFee,
    onDemandDiscount,
    onDemandDiscountReason,
    onDemandDiscountReasonId
  }) {
    var _a;
    const data = await this.cartApi.put("/api/v2/carts/items/price-manual", {
      itemId,
      consultationFee,
      onDemandDiscount,
      onDemandDiscountReason,
      onDemandDiscountReasonId
    }, {
      params: this.getParams()
    });
    return (_a = data == null ? void 0 : data.data) == null ? void 0 : _a.data;
  }
  async applyDeliveryService({
    serviceId
  }) {
    var _a;
    const res = await this.cartApi.put("/api/v2/carts/delivery-services", { serviceId }, { params: this.getParams() });
    return ((_a = res.data) == null ? void 0 : _a.data) || defaultCart;
  }
  async applyAdditionalService({
    serviceId,
    isEnable
  }) {
    var _a;
    const res = await this.cartApi.put("/api/v2/carts/additional-services", { serviceId, isEnable }, { params: this.getParams() });
    return ((_a = res.data) == null ? void 0 : _a.data) || defaultCart;
  }
  async applyPaymentMethodToCart({
    paymentMethodCode
  }) {
    var _a;
    const res = await this.cartApi.put("/api/v2/carts/payment-methods", { paymentMethodCode }, {
      params: this.getParams()
    });
    return ((_a = res == null ? void 0 : res.data) == null ? void 0 : _a.data) || defaultCart;
  }
  async requestItemSku({
    name,
    quantity
  }) {
    const { data, headers } = await this.cartApi.post("api/v2/carts/items/sku-request", { name, quantity }, {
      params: this.getParams()
    });
    const token = headers[cartTokenHeaderKey];
    if (!!token) {
      setCartToken(token);
    }
    return (data == null ? void 0 : data.data) || defaultCart;
  }
  async checkoutOrder({
    order
  }, cartToken) {
    var _a;
    const res = await this.cartApi.post("/api/v2/carts/checkout", { order }, {
      params: this.getParams(),
      headers: cartToken ? { [cartTokenHeaderKey]: cartToken } : {}
    });
    return (_a = res.data) == null ? void 0 : _a.data;
  }
  async removeItem(removeItemRequest) {
    var _a;
    const { itemId } = removeItemRequest;
    const params = this.getParams();
    const res = await this.cartApi.delete("/api/v2/carts/items", { itemId }, { params });
    return ((_a = res == null ? void 0 : res.data) == null ? void 0 : _a.data) || defaultCart;
  }
  async updateItemQuantity(updateItemQuantityRequest) {
    var _a;
    const { itemId, quantity } = updateItemQuantityRequest;
    const body = {
      itemId,
      quantity
    };
    const params = this.getParams();
    const res = await this.cartApi.put("/api/v2/carts/items/quantity", body, {
      params
    });
    return ((_a = res == null ? void 0 : res.data) == null ? void 0 : _a.data) || defaultCart;
  }
  async updateItemNote(updateItemNoteRequest) {
    var _a;
    const res = await this.cartApi.put("/api/v2/carts/items/note", updateItemNoteRequest, {
      params: this.getParams()
    });
    return ((_a = res == null ? void 0 : res.data) == null ? void 0 : _a.data) || defaultCart;
  }
  async selectItems(selectItemsRequest) {
    var _a;
    const { itemIds, isSelected } = selectItemsRequest;
    const body = {
      itemIds,
      isSelected
    };
    const params = this.getParams();
    const res = await this.cartApi.put("/api/v2/carts/items/selection", body, {
      params
    });
    return ((_a = res == null ? void 0 : res.data) == null ? void 0 : _a.data) || defaultCart;
  }
  async addItems(addItemsRequest, cartToken) {
    const {
      groups,
      override,
      overrideItemId,
      overrideTypes,
      isolatedCartType,
      isSkipHoldInventory
    } = addItemsRequest;
    const body = {
      groups,
      override,
      overrideTypes,
      overrideItemId,
      isolatedCartType,
      isSkipHoldInventory
    };
    const params = this.getParams();
    const { data, headers } = await this.cartApi.post("/api/v2/carts/items", body, {
      params,
      headers: cartToken ? { [cartTokenHeaderKey]: cartToken } : {}
    });
    const token = headers[cartTokenHeaderKey];
    if (!!token && !cartToken) {
      if (!isolatedCartType) {
        setCartToken(token);
      } else {
        storeBuyNowCartKey({
          key: getBuyNowKey(),
          createdAt: new Date().getTime(),
          cartToken: token
        });
        if (!!isSkipHoldInventory) {
          removeOldBuyNowCart();
          storeCartTokenHamper(token);
        }
      }
    }
    return (data == null ? void 0 : data.data) || defaultCart;
  }
  async recordToPayment({
    payments,
    orderId
  }) {
    const body = { payments, orderId };
    const params = this.getParams();
    const { data } = await this.cartApi.post("/api/v2/carts/record-to-payment", body, {
      params
    });
    return data.data;
  }
  async reCheckout(orderToken) {
    const params = this.getParams();
    const { data } = await this.cartApi.post("/api/v2/carts/checkout-to-payment", { orderToken }, { params });
    return data.data;
  }
  async getOrderPaymentMethod(orderId) {
    var _a;
    const res = await this.cartApi.get("api/v2/orders/payment-info", {
      params: __spreadProps(__spreadValues({}, this.getParams()), { orderId })
    });
    return (_a = res.data) == null ? void 0 : _a.data;
  }
  async updateCartGeneralInfo(payload, cartToken) {
    const res = await this.cartApi.put("/api/v2/carts", payload, {
      params: this.getParams(),
      headers: cartToken ? { [cartTokenHeaderKey]: cartToken } : {}
    });
    return res == null ? void 0 : res.data.data;
  }
  async applyPaymentAmount(payload) {
    var _a;
    const res = await this.cartApi.put("/api/v2/carts/payment-amount", payload, {
      params: this.getParams()
    });
    return ((_a = res == null ? void 0 : res.data) == null ? void 0 : _a.data) || defaultCart;
  }
  async getPriceAndPromotion(sku, params) {
    const { data } = await this.cartApi.get(`/api/v2/products/${sku}`, {
      params: __spreadValues(__spreadValues({}, this.getParams()), params)
    });
    return data.data;
  }
  async calculatePrice(body) {
    const { data } = await this.cartApi.post("/api/v2/products/calculate-price", body, { params: this.getParams() });
    return data.data;
  }
}

export { CartService, parseJWT, parseTerminalId, parseToken };
